import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DataService} from '../../Services/data.service';
import {StorageService} from '../../Services/storage.service';
import {DatePipe} from '@angular/common';
import {DateFilterService} from '../../Services/dateFilter.service';
import {BsToastService} from '../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {PaymentDraftService} from '../../Services/payment-draft.service';

@Component({
  selector: 'app-ar-dashboard',
  templateUrl: './ar-dashboard.component.html',
  styleUrls: ['./ar-dashboard.component.css'],
  standalone: false
})
export class ArDashboardComponent implements OnInit {

  vm: any = {};
  permissions: any = {};

  // KPI aging buckets
  bucket0to30 = 0;
  bucket31to60 = 0;
  bucket61to90 = 0;
  bucket90plus = 0;
  totalPastDue = 0;

  // CRM KPI tiles
  crmInteractionsThisWeek = 0;
  crmFollowUpsDueToday = 0;
  crmOverdueCustomerCount = 0;
  crmDso = 0;

  // Upcoming / past-due AR invoices
  upcomingDays = 30;
  pastDueItems: any[] = [];
  pastDueApiTotal = 0;
  upcomingItems: any[] = [];
  upcomingApiTotal = 0;
  loadingUpcoming = false;

  // Past Due filter + pagination + selection
  pastDueFilter = '';
  pastDuePage = 0;
  pastDuePageSize = 20;
  pastDueSelected = new Set<string>();

  // Upcoming filter + pagination + selection
  upcomingFilter = '';
  upcomingPage = 0;
  upcomingPageSize = 20;
  upcomingSelected = new Set<string>();
  isLoading = true;

  constructor(
    private tableDataService: DataService,
    private localStorage: StorageService,
    private datePipe: DatePipe,
    private dateFilter: DateFilterService,
    private notification: BsToastService,
    private titleService: Title,
    private router: Router,
    private paymentDraft: PaymentDraftService
  ) {
    this.permissions = JSON.parse(this.localStorage.StorageGet('Permissions', ''));
  }

  ngOnInit() {
    this.titleService.setTitle('AR Dashboard');
    if (this.permissions.Admin || this.permissions.ReceivableAging) {
      this.loadAgingBuckets();
      this.loadUpcoming();
      this.loadCrmKpis();
    } else {
      this.isLoading = false;
    }
  }

  loadAgingBuckets() {
    const toDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy');
    this.tableDataService.getData(
      'aging?toDate=' + toDate + '&subLedger=ar&company_id=&entity_id=&entity_type_id=&includePaid=false&pastDueOnly=false'
    ).subscribe((result: any[]) => {
      this.bucket0to30 = 0;
      this.bucket31to60 = 0;
      this.bucket61to90 = 0;
      this.bucket90plus = 0;

      const today = new Date();
      for (const entity of result) {
        for (const inv of entity.invoices) {
          const due = new Date(inv.due_date || inv.dueDate);
          const daysLate = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
          const amt = inv.owing || 0;
          if (daysLate <= 30) {
            this.bucket0to30 += amt;
          } else if (daysLate <= 60) {
            this.bucket31to60 += amt;
          } else if (daysLate <= 90) {
            this.bucket61to90 += amt;
          } else {
            this.bucket90plus += amt;
          }
        }
      }
      this.totalPastDue = this.bucket0to30 + this.bucket31to60 + this.bucket61to90 + this.bucket90plus;
      if (!this.loadingUpcoming) {
        this.isLoading = false;
      }
    });
  }

  loadUpcoming() {
    this.loadingUpcoming = true;
    const upcomingUrl = 'aging/upcoming-due?subLedger=ar&days=' + this.upcomingDays;
    this.tableDataService.getData(upcomingUrl).subscribe({
      next: (result: any) => {
        console.log('AR dashboard worklist result:', result);
        this.pastDueItems = result?.pastDue ?? [];
        this.pastDueApiTotal = result?.pastDueTotal ?? 0;
        this.upcomingItems = result?.upcoming ?? [];
        this.upcomingApiTotal = result?.upcomingTotal ?? 0;
        this.loadingUpcoming = false;
        this.isLoading = false;
      },
      error: () => {
        this.loadingUpcoming = false;
        this.isLoading = false;
      }
    });
  }

  onUpcomingDaysChange() {
    this.pastDuePage = 0;
    this.loadUpcoming();
  }

  get pastDueFiltered(): any[] {
    const q = this.pastDueFilter.trim().toLowerCase();
    if (!q) {
      return this.pastDueItems;
    }
    return this.pastDueItems.filter(i =>
      (i.entity_name ?? '').toLowerCase().includes(q) ||
      (i.number ?? '').toLowerCase().includes(q)
    );
  }

  get pastDueSorted(): any[] {
    return [...this.pastDueFiltered].sort((a, b) => {
      const dateA = new Date(a.due_date ?? a.dueDate ?? 0).getTime();
      const dateB = new Date(b.due_date ?? b.dueDate ?? 0).getTime();
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      return (a.entity_name ?? '').localeCompare(b.entity_name ?? '');
    });
  }

  get pastDuePaged(): any[] {
    const start = this.pastDuePage * this.pastDuePageSize;
    return this.pastDueSorted.slice(start, start + this.pastDuePageSize);
  }

  get pastDueTotalPages(): number {
    return Math.ceil(this.pastDueFiltered.length / this.pastDuePageSize) || 1;
  }

  get pastDuePageEnd(): number {
    return Math.min((this.pastDuePage + 1) * this.pastDuePageSize, this.pastDueFiltered.length);
  }

  onPastDueFilterChange(): void {
    this.pastDuePage = 0;
  }

  get pastDueSelectedEntityId(): string | null {
    if (this.pastDueSelected.size === 0) {
      return null;
    }
    const firstKey = this.pastDueSelected.values().next().value;
    const item = this.pastDueItems.find(i => String(i.document_id) === firstKey);
    return item ? String(item.entity_id) : null;
  }

  togglePastDueSelection(key: string, entityId: any): void {
    if (this.pastDueSelected.has(key)) {
      this.pastDueSelected.delete(key);
    } else {
      const currentEntity = this.pastDueSelectedEntityId;
      if (currentEntity !== null && currentEntity !== String(entityId)) {
        return;
      }
      this.pastDueSelected.add(key);
    }
  }

  get pastDueAllSelected(): boolean {
    const entityId = this.pastDueSelectedEntityId;
    const sameEntity = entityId ? this.pastDueSorted.filter(i => String(i.entity_id) === entityId) : this.pastDueSorted;
    return sameEntity.length > 0 && sameEntity.every(i => this.pastDueSelected.has(String(i.document_id)));
  }

  togglePastDueAll(): void {
    if (this.pastDueAllSelected) {
      this.pastDueSorted.forEach(i => this.pastDueSelected.delete(String(i.document_id)));
    } else {
      const entityId = this.pastDueSelectedEntityId;
      const sameEntity = entityId ? this.pastDueSorted.filter(i => String(i.entity_id) === entityId) : this.pastDueSorted;
      sameEntity.forEach(i => this.pastDueSelected.add(String(i.document_id)));
    }
  }

  paySelectedPastDue(): void {
    const selected = this.pastDueSorted.filter(i => this.pastDueSelected.has(String(i.document_id)));
    if (!selected.length) {
      return;
    }
    const first = selected[0];
    this.paymentDraft.set({
      entityId: first.entity_id,
      entityName: first.entity_name,
      paymentId: 'newAR',
      invoices: selected.map(i => ({
        id: i.document_id,
        number: i.number,
        entity_id: i.entity_id,
        entity_name: i.entity_name,
        account_id: 'ar',
        detail_total: i.balance,
        payment_applied_total: 0,
        due_date: i.due_date
      }))
    });
    this.router.navigate(['/transactions/payment/form'], {queryParams: {paymentId: 'newAR', entity_id: first.entity_id}});
  }

  get upcomingFiltered(): any[] {
    const q = this.upcomingFilter.trim().toLowerCase();
    if (!q) {
      return this.upcomingItems;
    }
    return this.upcomingItems.filter(i =>
      (i.entity_name ?? '').toLowerCase().includes(q) ||
      (i.number ?? '').toLowerCase().includes(q)
    );
  }

  get upcomingSorted(): any[] {
    return [...this.upcomingFiltered].sort((a, b) => {
      const dateA = new Date(a.due_date ?? 0).getTime();
      const dateB = new Date(b.due_date ?? 0).getTime();
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      return (a.entity_name ?? '').localeCompare(b.entity_name ?? '');
    });
  }

  get upcomingPaged(): any[] {
    const start = this.upcomingPage * this.upcomingPageSize;
    return this.upcomingSorted.slice(start, start + this.upcomingPageSize);
  }

  get upcomingTotalPages(): number {
    return Math.ceil(this.upcomingFiltered.length / this.upcomingPageSize) || 1;
  }

  get upcomingPageEnd(): number {
    return Math.min((this.upcomingPage + 1) * this.upcomingPageSize, this.upcomingFiltered.length);
  }

  onUpcomingFilterChange(): void {
    this.upcomingPage = 0;
  }

  get upcomingSelectedEntityId(): string | null {
    if (this.upcomingSelected.size === 0) {
      return null;
    }
    const firstKey = this.upcomingSelected.values().next().value;
    const item = this.upcomingItems.find(i => String(i.document_id) === firstKey);
    return item ? String(item.entity_id) : null;
  }

  toggleUpcomingSelection(key: string, entityId: any): void {
    if (this.upcomingSelected.has(key)) {
      this.upcomingSelected.delete(key);
    } else {
      const currentEntity = this.upcomingSelectedEntityId;
      if (currentEntity !== null && currentEntity !== String(entityId)) {
        return;
      }
      this.upcomingSelected.add(key);
    }
  }

  get upcomingAllSelected(): boolean {
    const entityId = this.upcomingSelectedEntityId;
    const sameEntity = entityId ? this.upcomingSorted.filter(i => String(i.entity_id) === entityId) : this.upcomingSorted;
    return sameEntity.length > 0 && sameEntity.every(i => this.upcomingSelected.has(String(i.document_id)));
  }

  toggleUpcomingAll(): void {
    if (this.upcomingAllSelected) {
      this.upcomingSorted.forEach(i => this.upcomingSelected.delete(String(i.document_id)));
    } else {
      const entityId = this.upcomingSelectedEntityId;
      const sameEntity = entityId ? this.upcomingSorted.filter(i => String(i.entity_id) === entityId) : this.upcomingSorted;
      sameEntity.forEach(i => this.upcomingSelected.add(String(i.document_id)));
    }
  }

  paySelectedUpcoming(): void {
    const selected = this.upcomingSorted.filter(i => this.upcomingSelected.has(String(i.document_id)));
    if (!selected.length) {
      return;
    }
    const first = selected[0];
    this.paymentDraft.set({
      entityId: first.entity_id,
      entityName: first.entity_name,
      paymentId: 'newAR',
      invoices: selected.map(i => ({
        id: i.document_id,
        number: i.number,
        entity_id: i.entity_id,
        entity_name: i.entity_name,
        account_id: 'ar',
        detail_total: i.balance,
        payment_applied_total: 0,
        due_date: i.due_date
      }))
    });
    this.router.navigate(['/transactions/payment/form'], {queryParams: {paymentId: 'newAR', entity_id: first.entity_id}});
  }

  upcomingPrevPage(): void {
    if (this.upcomingPage > 0) {
      this.upcomingPage--;
    }
  }

  upcomingNextPage(): void {
    if ((this.upcomingPage + 1) < this.upcomingTotalPages) {
      this.upcomingPage++;
    }
  }

  pastDuePrevPage(): void {
    if (this.pastDuePage > 0) {
      this.pastDuePage--;
    }
  }

  pastDueNextPage(): void {
    if ((this.pastDuePage + 1) < this.pastDueTotalPages) {
      this.pastDuePage++;
    }
  }

  loadCrmKpis(): void {
    const id = this.localStorage.StorageGet('default_company_id', '');
    const cp = id ? '?company_id=' + encodeURIComponent(id) : '';
    this.tableDataService.getData('crm/ar/kpis' + cp).subscribe({
      next: (result: any) => {
        if (result) {
          this.crmInteractionsThisWeek = result.interactionsThisWeek ?? 0;
          this.crmFollowUpsDueToday = result.followUpsDueToday ?? 0;
          this.crmOverdueCustomerCount = result.overdueCustomerCount ?? 0;
          this.crmDso = result.dso ?? 0;
        }
      },
      error: () => {
      }
    });
  }
}
